import { useCallback, useEffect, useState } from 'react';
import { Button, Pagination, TextField } from '@tryhackme/thm-ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useDebouncedCallback } from 'use-debounce';
import { TagColours, TagFormData, TagsProps } from 'src/features/admin/tagging/tagging.types';
import { StyledTagCards, StyledTagsHeader, StyledTagFilters, StyledInput, StyledSearch } from 'src/features/admin/tagging/tagging.styles';
import { TagCard } from 'src/features/admin/tagging/components/cards/tag-card';
import { DeleteModal } from 'src/common/delete-modal';
import { FormProvider, useForm } from 'src/common/form';
import { useTagFormResolver } from 'src/features/admin/tagging/hooks/use-tag-form-resolver';
import { AddTagModal } from '../modals/add-tag-modal';
export const Tags = ({
  handleCreateTag,
  handleDeleteTag,
  handleUpdateTag,
  isCreating,
  createdTag,
  resetCreateTag,
  tags,
  tagCategories,
  selectedCategoryId,
  selectedSubcategoryId,
  setSelectedCategoryId,
  setSelectedSubcategoryId,
  totalPages,
  onPageChange,
  currentPage,
  onLabelChange
}: TagsProps) => {
  const [addTagModalOpen, setAddTagModalOpen] = useState(false);
  const [deleteTagModalOpen, setDeleteTagModalOpen] = useState(false);
  const [activeTagIndex, setActiveTagIndex] = useState(0);
  const [activeTagId, setActiveTagId] = useState<string>();
  const handleModalDelete = useCallback(() => {
    if (activeTagId) {
      handleDeleteTag(activeTagId);
    }
    setDeleteTagModalOpen(false);
  }, [activeTagId, handleDeleteTag]);
  const categoryFieldOptions = tagCategories.map(tc => ({
    value: tc._id,
    label: tc.label
  }));
  const [selectedSubcategories, setSelectedSubcategories] = useState<{
    value: string;
    label: string;
  }[]>([]);
  useEffect(() => {
    setSelectedSubcategories(tagCategories.find(tagCategory => tagCategory._id === selectedCategoryId)?.subcategories.map(tc => ({
      value: tc._id,
      label: tc.label
    })) ?? []);
  }, [tagCategories, selectedCategoryId]);
  const addTagForm = useForm<TagFormData>({
    defaultValues: {
      colour: Object.entries(TagColours)[0],
      weight: 1
    },
    resolver: useTagFormResolver(),
    mode: 'all'
  });
  useEffect(() => {
    if (createdTag && !isCreating) {
      setAddTagModalOpen(false);
      addTagForm.reset();
      addTagForm.clearErrors();
      resetCreateTag();
    }
  }, [addTagForm, createdTag, isCreating, resetCreateTag]);
  const debouncedLabelChange = useDebouncedCallback((value: string) => {
    const trimmedValue = value.trim().replace(/\s+/g, ' ');
    onLabelChange(trimmedValue);
  }, 500, {
    leading: false,
    trailing: true
  });
  const handleSearchLabelChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    debouncedLabelChange(newValue);
  }, [debouncedLabelChange]);
  return <StyledTagCards data-sentry-element="StyledTagCards" data-sentry-component="Tags" data-sentry-source-file="tags.tsx">
      <StyledTagsHeader data-sentry-element="StyledTagsHeader" data-sentry-source-file="tags.tsx">
        <StyledTagFilters data-sentry-element="StyledTagFilters" data-sentry-source-file="tags.tsx">
          <StyledInput data-sentry-element="StyledInput" data-sentry-source-file="tags.tsx">
            <TextField variant="select" isMulti={false} isSearchable={false} options={categoryFieldOptions} selected={categoryFieldOptions.find(option => option.value === selectedCategoryId)} handleSelection={selection => {
            setSelectedCategoryId(selection.value);
            setSelectedSubcategoryId('');
          }} data-sentry-element="TextField" data-sentry-source-file="tags.tsx" />
          </StyledInput>
          <StyledInput data-sentry-element="StyledInput" data-sentry-source-file="tags.tsx">
            <TextField variant="select" isDisabled={selectedSubcategories.length === 0} isMulti={false} isSearchable={false} options={selectedSubcategories} selected={selectedSubcategories.find(option => option.value === selectedSubcategoryId)} handleSelection={selection => setSelectedSubcategoryId(selection.value)} placeholder={selectedSubcategories.length === 0 ? 'No subcategories' : 'Select tag subcategory'} data-sentry-element="TextField" data-sentry-source-file="tags.tsx" />
          </StyledInput>
          <StyledSearch data-sentry-element="StyledSearch" data-sentry-source-file="tags.tsx">
            <TextField placeholder="Search for a tag" variant="input" leftAdornment={<FontAwesomeIcon icon={faSearch} />} onChange={handleSearchLabelChange} data-sentry-element="TextField" data-sentry-source-file="tags.tsx" />
          </StyledSearch>
        </StyledTagFilters>
        <Button onClick={() => setAddTagModalOpen(true)} data-sentry-element="Button" data-sentry-source-file="tags.tsx">Add Tag</Button>
      </StyledTagsHeader>
      {tags.map((tag, index) => <TagCard key={`${tag._id}-${tag.label}`} tag={tag} handleDeleteTag={() => {
      setActiveTagId(tag._id);
      setActiveTagIndex(index);
      setDeleteTagModalOpen(true);
    }} tagCategories={tagCategories} handleUpdateTag={handleUpdateTag} />)}
      {totalPages > 1 && <Pagination totalPages={totalPages} selectedPage={currentPage} onChange={onPageChange} />}
      <FormProvider {...addTagForm} data-sentry-element="FormProvider" data-sentry-source-file="tags.tsx">
        <AddTagModal isOpen={addTagModalOpen} setIsOpen={setAddTagModalOpen} handleCreateTag={handleCreateTag} isCreating={isCreating} tagCategories={tagCategories} data-sentry-element="AddTagModal" data-sentry-source-file="tags.tsx" />
      </FormProvider>
      <DeleteModal open={deleteTagModalOpen} onOpenChange={setDeleteTagModalOpen} modalTitleText="Delete Tag" modalBodyText={<div>
            <p>Deleting a tag will also remove it from anywhere it&apos;s used across THM.</p>
            <br />
            <p>Are you sure you want to delete this tag?</p>
          </div>} activeIndex={activeTagIndex} handleDeleteAction={handleModalDelete} data-sentry-element="DeleteModal" data-sentry-source-file="tags.tsx" />
    </StyledTagCards>;
};